'use client'

import {useCallback, useEffect, useMemo, useState} from 'react'
import {Clock, Pencil, RefreshCw, Trash2, X} from 'lucide-react'
import type {Contact, ContactLabel, ReplyPayload, ScheduledTask} from '@/types'
import {displayName, emptyReplyPayload, payloadText} from '@/lib/utils'
import {resolveTargets} from '@/lib/targetResolution'
import {PageHeader} from '@/components/PageHeader'
import {ContactSelectorPanel} from '@/components/ContactSelectorPanel'
import {ReplyPayloadEditor} from '@/components/ReplyPayloadEditor'
import type {TargetItem} from '@/types'

function targetDisplay(target: string | TargetItem[]): string {
    if (Array.isArray(target)) {
        return target.map((t) => t.name || t.wxid).join('、')
    }
    try {
        const arr = JSON.parse(target)
        if (Array.isArray(arr)) {
            return arr.map((t: TargetItem) => t.name || t.wxid).join('、')
        }
    } catch { /* ignore */ }
    return target
}

function parseTargetItems(target: string | TargetItem[]): TargetItem[] {
    if (Array.isArray(target)) return target
    try {
        const arr = JSON.parse(target)
        if (Array.isArray(arr)) return arr
    } catch { /* ignore */ }
    return []
}

type ScheduleType = NonNullable<ScheduledTask['scheduleType']>
type IntervalUnit = 'minute' | 'hour' | 'day'

const statusMeta: Record<ScheduledTask['status'], { label: string; className: string }> = {
    sent: {label: '已发送', className: 'bg-green-50 text-green-600'},
    pending: {label: '待发送', className: 'bg-orange-50 text-orange-600'},
    failed: {label: '失败', className: 'bg-red-50 text-red-600'},
}

const scheduleTypes: Array<{ value: ScheduleType; label: string }> = [
    {value: 'once', label: '指定时间一次'},
    {value: 'fixed_rate', label: '固定频率'},
    {value: 'fixed_delay', label: '固定间隔'},
    {value: 'weekly', label: '指定星期'},
    {value: 'monthly', label: '每月某天'},
]

const intervalUnits: Array<{ value: IntervalUnit; label: string; seconds: number }> = [
    {value: 'minute', label: '分钟', seconds: 60},
    {value: 'hour', label: '小时', seconds: 3600},
    {value: 'day', label: '天', seconds: 86400},
]

const weekOptions = [
    {value: 1, label: '周一'},
    {value: 2, label: '周二'},
    {value: 3, label: '周三'},
    {value: 4, label: '周四'},
    {value: 5, label: '周五'},
    {value: 6, label: '周六'},
    {value: 0, label: '周日'},
]

function scheduledTaskText(task: ScheduledTask): string {
    return payloadText(task.payload || {type: 'text', content: task.content})
}

function formatInterval(seconds?: number): string {
    if (!seconds) return ''
    if (seconds % 86400 === 0) return `${seconds / 86400} 天`
    if (seconds % 3600 === 0) return `${seconds / 3600} 小时`
    if (seconds % 60 === 0) return `${seconds / 60} 分钟`
    return `${seconds} 秒`
}

function scheduleSummary(task: ScheduledTask): string {
    const type = task.scheduleType || 'once'
    if (type === 'fixed_rate') return `固定频率：每 ${formatInterval(task.intervalSeconds)} 执行`
    if (type === 'fixed_delay') return `固定间隔：发送完成后 ${formatInterval(task.intervalSeconds)} 再执行`
    if (type === 'weekly') {
        const labels = (task.weekdays || [])
            .map((weekday) => weekOptions.find((item) => item.value === weekday)?.label)
            .filter(Boolean)
            .join('、')
        return `指定星期：${labels || '-'}`
    }
    if (type === 'monthly') return `每月 ${task.monthDay || '-'} 日执行`
    return '一次性任务'
}

export function ScheduledView({
    contacts,
    labels,
    tasks,
    onSaveTask,
    onDeleteTask,
    onRefresh,
    voiceId,
}: {
    contacts: Contact[]
    labels: ContactLabel[]
    tasks: ScheduledTask[]
    onSaveTask: (task: Partial<ScheduledTask>) => Promise<void> | void
    onDeleteTask: (id: number) => void
    onRefresh?: () => Promise<void> | void
    voiceId?: string
}) {
    const [selectedWxids, setSelectedWxids] = useState<string[]>([])
    const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>([])
    const [scheduleType, setScheduleType] = useState<ScheduleType>('once')
    const [sendAt, setSendAt] = useState('')
    const [intervalValue, setIntervalValue] = useState(10)
    const [intervalUnit, setIntervalUnit] = useState<IntervalUnit>('minute')
    const [weekdays, setWeekdays] = useState<number[]>([])
    const [monthDay, setMonthDay] = useState(new Date().getDate())
    const [payload, setPayload] = useState<ReplyPayload>(() => emptyReplyPayload())
    const [refreshing, setRefreshing] = useState(false)
    const [saving, setSaving] = useState(false)
    const [editingTaskId, setEditingTaskId] = useState<number | null>(null)

    // 点击任务回显表单
    const handleEditTask = useCallback((task: ScheduledTask) => {
        const items = parseTargetItems(task.target)
        setSelectedWxids(items.map((t) => t.wxid))
        setSelectedLabelIds([])
        setScheduleType((task.scheduleType || 'once') as ScheduleType)
        setSendAt(task.sendAt ? new Date(task.sendAt - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '')
        if (task.intervalSeconds) {
            if (task.intervalSeconds % 86400 === 0) {
                setIntervalValue(task.intervalSeconds / 86400)
                setIntervalUnit('day')
            } else if (task.intervalSeconds % 3600 === 0) {
                setIntervalValue(task.intervalSeconds / 3600)
                setIntervalUnit('hour')
            } else {
                setIntervalValue(task.intervalSeconds / 60)
                setIntervalUnit('minute')
            }
        } else {
            setIntervalValue(10)
            setIntervalUnit('minute')
        }
        setWeekdays(task.weekdays || [])
        setMonthDay(task.monthDay || new Date().getDate())
        setPayload(task.payload || emptyReplyPayload())
        setEditingTaskId(task.id)
    }, [])

    const handleCancelEdit = useCallback(() => {
        setEditingTaskId(null)
        setSelectedWxids([])
        setSelectedLabelIds([])
        setSendAt('')
        setWeekdays([])
        setPayload(emptyReplyPayload())
    }, [])

    useEffect(() => {
        if (!onRefresh) return
        void Promise.resolve(onRefresh())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleRefresh = async () => {
        if (!onRefresh || refreshing) return
        setRefreshing(true)
        try {
            await onRefresh()
        } finally {
            setRefreshing(false)
        }
    }

    const resolvedTargets = useMemo(
        () => resolveTargets({contactWxids: selectedWxids, labelIds: selectedLabelIds, contacts}),
        [selectedWxids, selectedLabelIds, contacts],
    )

    const content = payloadText(payload).trim()
    const intervalSeconds =
        intervalValue * (intervalUnits.find((item) => item.value === intervalUnit)?.seconds || 60)
    const scheduleReady =
        scheduleType === 'fixed_rate' || scheduleType === 'fixed_delay'
            ? intervalValue > 0
            : scheduleType === 'weekly'
              ? weekdays.length > 0
              : scheduleType === 'monthly'
                ? monthDay >= 1 && monthDay <= 31
                : true
    const canSave = resolvedTargets.length > 0 && !!sendAt && !!content && scheduleReady && !saving

    const handleSave = async () => {
        if (!canSave) return
        setSaving(true)
        try {
            // 一个任务支持多个接收人
            const target = resolvedTargets.map((wxid) => {
                const contact = contacts.find((c) => c.wxid === wxid)
                return { wxid, name: contact ? displayName(contact) : wxid }
            })
            const task: Partial<ScheduledTask> = {
                target,
                sendAt: new Date(sendAt).getTime(),
                content,
                payload,
                scheduleType,
                intervalSeconds:
                    scheduleType === 'fixed_rate' || scheduleType === 'fixed_delay'
                        ? intervalSeconds
                        : undefined,
                weekdays: scheduleType === 'weekly' ? weekdays : undefined,
                monthDay: scheduleType === 'monthly' ? monthDay : undefined,
                status: 'pending',
            }
            if (editingTaskId) {
                task.id = editingTaskId
            }
            await onSaveTask(task)
            setEditingTaskId(null)
            setSelectedWxids([])
            setSelectedLabelIds([])
            setSendAt('')
            setWeekdays([])
            setPayload(emptyReplyPayload())
        } finally {
            setSaving(false)
        }
    }

    const addContact = (wxid: string) => {
        setSelectedWxids((prev) => (prev.includes(wxid) ? prev : [...prev, wxid]))
    }
    const removeContact = (wxid: string) => {
        setSelectedWxids((prev) => prev.filter((item) => item !== wxid))
    }
    const addLabel = (id: number) => {
        setSelectedLabelIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    }
    const removeLabel = (id: number) => {
        setSelectedLabelIds((prev) => prev.filter((item) => item !== id))
    }

    const sortedTasks = useMemo(
        () => [...tasks].sort((a, b) => (b.nextRunAt || b.sendAt) - (a.nextRunAt || a.sendAt)),
        [tasks],
    )

    const toggleWeekday = (weekday: number) => {
        setWeekdays((prev) =>
            prev.includes(weekday)
                ? prev.filter((item) => item !== weekday)
                : [...prev, weekday].sort((a, b) => a - b),
        )
    }

    return (
        <section className="flex flex-col h-full bg-page">
            <PageHeader
                title="定时发送消息"
                action={
                    onRefresh ? (
                        <button
                            type="button"
                            className="btn-secondary"
                            disabled={refreshing}
                            onClick={handleRefresh}
                            title="刷新任务状态"
                        >
                            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''}/>
                            刷新
                        </button>
                    ) : null
                }
            />
            <div className="flex-1 flex flex-col overflow-y-auto p-3 md:p-6 gap-3 md:gap-6">
                <div className="flex min-h-0 flex-col lg:min-h-[560px] lg:flex-row gap-3 md:gap-6">
                    {/* Left — target selector panel */}
                    <ContactSelectorPanel
                    className="min-h-[420px] w-full lg:min-h-[560px] lg:w-[300px] shrink-0 bg-white rounded-card shadow-card border border-hair flex flex-col h-auto overflow-hidden relative"
                    contacts={contacts}
                    selectedWxids={selectedWxids}
                    onSelect={addContact}
                    onRemove={removeContact}
                    onClear={() => { setSelectedWxids([]); setSelectedLabelIds([]) }}
                    labels={labels}
                    selectedLabelIds={selectedLabelIds}
                    onLabelSelect={addLabel}
                    onLabelRemove={removeLabel}
                    placeholder="搜索并添加..."
                    />

                    {/* Middle — task form */}
                    <div className="flex-1 min-w-0 min-h-[520px] lg:min-h-[560px] bg-white rounded-card shadow-card border border-hair flex flex-col h-auto overflow-hidden">
                    <div className="p-4 border-b border-hair bg-gray-50 flex items-center justify-between">
                        <h3 className="text-sm font-medium text-ink">
                            {editingTaskId ? '编辑定时任务' : '新建定时任务'}
                        </h3>
                        {editingTaskId ? (
                            <button
                                type="button"
                                className="p-1 rounded text-muted hover:text-ink hover:bg-gray-200"
                                onClick={handleCancelEdit}
                                title="取消编辑"
                            >
                                <X size={16}/>
                            </button>
                        ) : null}
                    </div>
                    <div className="flex-1 overflow-y-auto p-5 space-y-5">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-soft">已选对象：</span>
                            <span className="text-xs text-accent font-medium">
                                共 {resolvedTargets.length} 个发送目标
                            </span>
                        </div>
                        <div className="space-y-2">
                            <label className="block text-xs text-soft">任务类型</label>
                            <select
                                className="input"
                                value={scheduleType}
                                onChange={(event) => setScheduleType(event.target.value as ScheduleType)}
                            >
                                {scheduleTypes.map((item) => (
                                    <option key={item.value} value={item.value}>{item.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="block text-xs text-soft">
                                {scheduleType === 'once' ? '发送时间' : '首次执行时间'}
                            </label>
                            <input
                                type="datetime-local"
                                className="input"
                                value={sendAt}
                                onChange={(event) => setSendAt(event.target.value)}
                            />
                        </div>
                        {scheduleType === 'fixed_rate' || scheduleType === 'fixed_delay' ? (
                            <div className="space-y-2">
                                <label className="block text-xs text-soft">执行间隔</label>
                                <div className="grid grid-cols-[1fr_112px] gap-2">
                                    <input
                                        type="number"
                                        min={1}
                                        className="input"
                                        value={intervalValue}
                                        onChange={(event) =>
                                            setIntervalValue(Math.max(1, Number(event.target.value) || 1))
                                        }
                                    />
                                    <select
                                        className="input"
                                        value={intervalUnit}
                                        onChange={(event) => setIntervalUnit(event.target.value as IntervalUnit)}
                                    >
                                        {intervalUnits.map((item) => (
                                            <option key={item.value} value={item.value}>{item.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ) : null}
                        {scheduleType === 'weekly' ? (
                            <div className="space-y-2">
                                <label className="block text-xs text-soft">执行星期</label>
                                <div className="flex flex-wrap gap-2">
                                    {weekOptions.map((item) => {
                                        const checked = weekdays.includes(item.value)
                                        return (
                                            <button
                                                key={item.value}
                                                type="button"
                                                className={`h-8 px-3 rounded-md border text-xs ${
                                                    checked
                                                        ? 'border-accent bg-accent/10 text-accent'
                                                        : 'border-hair bg-white text-soft hover:text-ink'
                                                }`}
                                                onClick={() => toggleWeekday(item.value)}
                                            >
                                                {item.label}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>
                        ) : null}
                        {scheduleType === 'monthly' ? (
                            <div className="space-y-2">
                                <label className="block text-xs text-soft">每月日期</label>
                                <input
                                    type="number"
                                    min={1}
                                    max={31}
                                    className="input"
                                    value={monthDay}
                                    onChange={(event) =>
                                        setMonthDay(Math.min(31, Math.max(1, Number(event.target.value) || 1)))
                                    }
                                />
                            </div>
                        ) : null}
                        <div className="space-y-2">
                            <label className="block text-xs text-soft">消息内容</label>
                            <div className="rounded-md border border-hair bg-white p-2">
                                <ReplyPayloadEditor
                                    compact
                                    payload={payload}
                                    onChange={setPayload}
                                    onSubmit={() => void handleSave()}
                                    placeholder="输入定时发送内容..."
                                    voiceId={voiceId}
                                    action={
                                        <button
                                            type="button"
                                            className="btn-primary w-full justify-center"
                                            onClick={() => void handleSave()}
                                            disabled={!canSave}
                                        >
                                            {saving ? '保存中...' : editingTaskId ? '更新任务' : '保存任务'}
                                        </button>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    </div>
                </div>

                {/* Right — task list */}
                <div className="w-full min-h-[260px] bg-white rounded-card shadow-card border border-hair flex flex-col h-auto overflow-hidden shrink-0">
                    <div className="p-4 border-b border-hair bg-gray-50 flex items-center justify-between">
                        <h3 className="text-sm font-medium text-ink">任务列表 ({tasks.length})</h3>
                    </div>
                    <div className="flex-1 overflow-auto p-3 space-y-3">
                        {sortedTasks.length ? (
                            sortedTasks.map((task) => {
                                const meta = statusMeta[task.status] || statusMeta.pending
                                const text = scheduledTaskText(task)
                                return (
                                    <div
                                        key={task.id}
                                        className={`rounded-lg p-3 space-y-2 cursor-pointer transition-colors ${
                                            editingTaskId === task.id
                                                ? 'bg-accent/5 border-2 border-accent'
                                                : 'bg-white border border-hair hover:border-gray-300'
                                        }`}
                                        onClick={() => handleEditTask(task)}
                                    >
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-medium text-ink truncate">
                                                发送给：{targetDisplay(task.target)}
                                            </span>
                                            <span className={`inline-flex items-center h-5 px-1.5 rounded text-[11px] shrink-0 ${meta.className}`}>
                                                {meta.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-muted">
                                            <Clock size={12}/>
                                            <span>{new Date(task.nextRunAt || task.sendAt).toLocaleString()}</span>
                                        </div>
                                        <div className="text-[11px] text-muted truncate" title={scheduleSummary(task)}>
                                            {scheduleSummary(task)}
                                        </div>
                                        <div className="text-xs text-soft truncate" title={text}>
                                            {text}
                                        </div>
                                        {task.lastError && task.status !== 'sent' ? (
                                            <div className="text-[11px] text-red-500 truncate" title={task.lastError}>
                                                失败：{task.lastError}
                                            </div>
                                        ) : null}
                                        <div className="flex justify-end gap-1">
                                            <button
                                                type="button"
                                                className={`p-1 rounded ${editingTaskId === task.id ? 'text-accent bg-accent/10' : 'text-muted hover:bg-gray-100 hover:text-ink'}`}
                                                title="编辑"
                                                onClick={() => handleEditTask(task)}
                                            >
                                                <Pencil size={14}/>
                                            </button>
                                            <button
                                                type="button"
                                                className="p-1 rounded text-muted hover:bg-red-50 hover:text-red-500"
                                                title="删除"
                                                onClick={() => onDeleteTask(task.id)}
                                            >
                                                <Trash2 size={14}/>
                                            </button>
                                        </div>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-center text-muted text-sm py-12">暂无定时任务</div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
